import { Injectable } from '@angular/core';
import { Actions, createEffect } from '@ngrx/effects';
import { interval, Observable, of } from 'rxjs';
import { Action } from '@ngrx/store';
import {
    filter,
    catchError,
    map,
    switchMap,
    startWith,
    takeUntil,
    exhaustMap,
    takeWhile
} from 'rxjs/operators';

import {
    ClearPollingInterval,
    GetAppMaintenanceModeRequestSuccess,
    GetAppMaintenanceModeStatusRequest,
    GetAppMaintenanceModeStatusRequestFailure,
    StartMaintenancePolling
} from './common.actions';
import { ApplicationService } from '../../../shared/services/application.service';
import { ApiResponse } from '../../../../../common/models/api-response.interface';

@Injectable()
export class CommonEffects {
    maintenanceModeStatus$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            filter(
                (action: Action) =>
                    action instanceof GetAppMaintenanceModeStatusRequest
            ),
            switchMap(() =>
                this.applicationService.getMaintenanceModeConfig().pipe(
                    switchMap(
                        (
                            response: ApiResponse<{
                                isMaintenanceMode?: boolean;
                                maintenanceModeDisplayMssg?: string;
                            }>
                        ) => {
                            const data = response?.data ?? {};
                            const { isMaintenanceMode } = data;

                            const success =
                                new GetAppMaintenanceModeRequestSuccess(data);

                            if (isMaintenanceMode) {
                                // dispatch both success + start polling
                                return of(
                                    success,
                                    new StartMaintenancePolling()
                                );
                            }

                            // maintenance off → only success
                            return of(success);
                        }
                    ),
                    catchError(() =>
                        of(new GetAppMaintenanceModeStatusRequestFailure())
                    )
                )
            )
        )
    );

    maintenancePolling$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            filter(
                (action: Action) => action instanceof StartMaintenancePolling
            ),
            exhaustMap(() =>
                interval(60000).pipe(
                    startWith(0), // fire immediately
                    switchMap(() =>
                        this.applicationService.getMaintenanceModeConfig().pipe(
                            map(
                                (
                                    response: ApiResponse<{
                                        isMaintenanceMode?: boolean;
                                        maintenanceModeDisplayMssg?: string;
                                    }>
                                ) =>
                                    new GetAppMaintenanceModeRequestSuccess(
                                        response?.data ?? {}
                                    )
                            ),
                            catchError(() =>
                                of(
                                    new GetAppMaintenanceModeStatusRequestFailure()
                                )
                            )
                        )
                    ),
                    // stop polling automatically when flag becomes false
                    takeWhile(
                        (action: GetAppMaintenanceModeRequestSuccess) =>
                            action.payload?.isMaintenanceMode === true,
                        true // include the final "false" action
                    ),
                    // also stop if user explicitly clears polling
                    takeUntil(
                        this.actions$.pipe(
                            filter((action: Action) => {
                                return action instanceof ClearPollingInterval;
                            })
                        )
                    )
                )
            )
        )
    );

    constructor(
        private actions$: Actions,
        private applicationService: ApplicationService
    ) {}
}
